import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Paper,
	Typography,
	TextField,
	Button,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	CircularProgress,
	Autocomplete,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import {
	IManagerInitiationData,
	EmployeeType,
	EmploymentType,
	RoleType,
} from '../interfaces/IOnboardingFlow';
import style from './ManagerInitiationForm.module.scss';
import { getUsersByWorkplace } from 'core/API/checklist';
import useUserDataStore from 'store/useUserDataStore';
import { getOnboardingSettings } from 'core/API/onboarding';
import { Workplace } from 'module/workplaces/interface/WorkplacesInterface';
import { IOnboardingSettings } from 'core/interfaces/onboardingInterface';
import { apiService } from 'core/services/apiService';

interface ManagerInitiationFormProps {
	workplaceId: string;
	onSubmit: (data: IManagerInitiationData) => Promise<void>;
	onCancel: () => void;
}

export function ManagerInitiationForm({
	workplaceId,
	onSubmit,
	onCancel,
}: ManagerInitiationFormProps): JSX.Element {
	// Clean hierarchy name: remove parenthetical content and standalone 'node'
	const cleanHierarchyName = (raw?: string) => {
		if (!raw) return '';
		let s = raw.replace(/\s*\([^)]*\)\s*/g, ' ');
		s = s.replace(/\bnode\b/gi, ' ');
		return s.replace(/\s+/g, ' ').trim();
	};

	const { securityRoles, hierarchyNodes, initializeFromLocalStorage } = useUserDataStore();

	const currentWorkplaceHierarchy = hierarchyNodes.find(
		(node) =>
			node.childId === Number(workplaceId) ||
			(node.type === 'workplace' && node.childId === Number(workplaceId))
	);
	
	// Get today's date in YYYY-MM-DD format for the employment start date default
	const getTodayDate = () => {
		const today = new Date();
		const year = today.getFullYear();
		const month = String(today.getMonth() + 1).padStart(2, '0');
		const day = String(today.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const [formData, setFormData] = useState<IManagerInitiationData>({
		employeeType: EmployeeType.NEW,
		firstName: '',
		middleName: '',
		lastName: '',
		email: '',
		cellPhone: '',
		employmentStartDate: getTodayDate(),
		employmentType: EmploymentType.HOURLY,
		roleType: RoleType.PART_TIME,
		securityRole: '',
		hierarchyAssignment: '',
	});

	const [employees, setEmployees] = useState<any[]>([]);
	const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [workplace, setWorkplace] = useState<Workplace | null>(null);
	const [onboardingSettings, setOnboardingSettings] = useState<IOnboardingSettings | null>(null);
	const [isLoadingWorkplaceInfo, setIsLoadingWorkplaceInfo] = useState(true);

	// Get security roles and hierarchy from localStorage via store

	// Find the current workplace's hierarchy node to preselect i

	const fetchEmployees = useCallback(async () => {
		setIsLoadingEmployees(true);
		try {
			const response = await getUsersByWorkplace(workplaceId);
			setEmployees(response);
		} catch (error) {
			console.error('Error fetching employees:', error);
			setEmployees([]);
		} finally {
			setIsLoadingEmployees(false);
		}
	}, [workplaceId]);

	// Fetch workplace and onboarding settings
	useEffect(() => {
		const fetchWorkplaceInfo = async () => {
			setIsLoadingWorkplaceInfo(true);
			try {
				// Try to get workplaces from cache first
				let workplacesMap: Record<string, Workplace> = {};
				const cachedWorkplaces = localStorage.getItem('cached-workplaces');
				
				if (cachedWorkplaces) {
					try {
						workplacesMap = JSON.parse(cachedWorkplaces);
					} catch (e) {
						console.error('Error parsing cached workplaces:', e);
					}
				}
				
				// If no cache or workplace not in cache, fetch all workplaces
				if (!workplacesMap[workplaceId]) {
					const response = await apiService.get('/workplaces');
					if (response.data?.member) {
						workplacesMap = response.data.member.reduce(
							(acc: Record<string, Workplace>, workplace: Workplace) => {
								acc[workplace.id.toString()] = workplace;
								return acc;
							},
							{}
						);
						// Cache the workplaces
						localStorage.setItem('cached-workplaces', JSON.stringify(workplacesMap));
					}
				}
				
				const workplaceData = workplacesMap[workplaceId];
				const settingsData = await getOnboardingSettings(Number(workplaceId));
				
				setWorkplace(workplaceData);
				setOnboardingSettings(settingsData);
			} catch (error) {
				console.error('Error fetching workplace info:', error);
			} finally {
				setIsLoadingWorkplaceInfo(false);
			}
		};

		fetchWorkplaceInfo();
	}, [workplaceId]);

	// Initialize data from localStorage on mount
	useEffect(() => {
		initializeFromLocalStorage();
	}, [initializeFromLocalStorage]);

	// Preselect current workplace hierarchy when hierarchy nodes are loaded
	useEffect(() => {
		if (currentWorkplaceHierarchy && !formData.hierarchyAssignment) {
			setFormData((prev) => ({
				...prev,
				hierarchyAssignment: currentWorkplaceHierarchy['@id'] || '',
			}));
		}
	}, []);

	// Fetch existing employees for rehire/existing selection
	useEffect(() => {
		if (formData.employeeType !== EmployeeType.NEW) {
			fetchEmployees();
		}
	}, [formData.employeeType, fetchEmployees]);

	const handleEmployeeSelect = (employee: any) => {
		if (employee) {
			setFormData({
				...formData,
				existingEmployeeId: employee.id,
				firstName: employee.firstName || '',
				lastName: employee.lastName || '',
				email: employee.email || '',
				cellPhone: employee.phone || '',
			});
		}
	};

	const handleInputChange = (field: keyof IManagerInitiationData, value: any) => {
		setFormData({
			...formData,
			[field]: value,
		});
		// Clear error for this field
		if (errors[field]) {
			setErrors({
				...errors,
				[field]: '',
			});
		}
	};

	const validateForm = (): boolean => {
		const newErrors: Record<string, string> = {};

		// Required fields validation
		if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
		if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
		if (!formData.email.trim()) newErrors.email = 'Email is required';
		else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
			newErrors.email = 'Invalid email format';
		if (!formData.cellPhone.trim()) newErrors.cellPhone = 'Cell phone is required';
		if (!formData.employmentStartDate) newErrors.employmentStartDate = 'Start date is required';
		if (!formData.securityRole) newErrors.securityRole = 'Security role is required';
		if (!formData.hierarchyAssignment)
			newErrors.hierarchyAssignment = 'Hierarchy assignment is required';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);
		try {
			await onSubmit(formData);
		} catch (error) {
			console.error('Error submitting form:', error);
		} finally {
			setIsSubmitting(false);
		}
	};
	console.log("workplace", workplace);

	return (
		<div className={style.FormContainer}>
			<div className={style.Header}>
				<div className={style.HeaderTop}>
					<Button onClick={onCancel} className={style.BackButton}>
						Back
					</Button>
				</div>
				<h1>Initiate Onboarding - Phase 1</h1>
			</div>

			{/* Workplace Information */}
			{isLoadingWorkplaceInfo ? (
				<Box sx={{ textAlign: 'center', py: 2 }}>
					<CircularProgress size={24} />
				</Box>
			) : workplace ? (
				<Paper sx={{ p: 2.5, mb: 2, backgroundColor: '#f8f9fa' }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#333' }}>
						Workplace Information
					</Typography>
					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
						<Box>
							<Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
								Workplace Name
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{workplace.name || 'N/A'}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
								Address
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{workplace.streetAddress && workplace.city
									? `${workplace.streetAddress}, ${workplace.city}${workplace.state ? ', ' + workplace.state : ''} ${workplace.postcode || ''}`
									: 'N/A'}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
								FEIN
							</Typography>
							<Typography variant="body2" sx={{ fontWeight: 500 }}>
								{onboardingSettings?.ein || 'Not configured'}
							</Typography>
						</Box>
					</Box>
				</Paper>
			) : null}

			<Paper className={style.FormPaper}>
				<form onSubmit={handleSubmit}>
					<Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
						Manager Initiation
					</Typography>

					{/* Employee Type Selection */}
					<FormControl fullWidth sx={{ mb: 3 }}>
						<InputLabel>Employee Type</InputLabel>
						<Select
							value={formData.employeeType}
							onChange={(e) => handleInputChange('employeeType', e.target.value)}
							label="Employee Type"
						>
							<MenuItem value={EmployeeType.NEW}>New Employee</MenuItem>
							<MenuItem value={EmployeeType.REHIRE}>Rehire</MenuItem>
							<MenuItem value={EmployeeType.EXISTING}>Existing Employee</MenuItem>
						</Select>
					</FormControl>

					{/* Existing Employee Selection (for Rehire/Existing) */}
					{formData.employeeType !== EmployeeType.NEW && (
						<Autocomplete
							options={employees}
							getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
							loading={isLoadingEmployees}
							onChange={(event, value) => handleEmployeeSelect(value)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Select Employee"
									margin="normal"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{isLoadingEmployees ? <CircularProgress size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							sx={{ mb: 3 }}
						/>
					)}

					{/* Basic Information */}
					<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
						Basic Information
					</Typography>

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
						<TextField
							label="First Name"
							value={formData.firstName}
							onChange={(e) => handleInputChange('firstName', e.target.value)}
							error={!!errors.firstName}
							helperText={errors.firstName}
							required
						/>
						<TextField
							label="Middle Name"
							value={formData.middleName}
							onChange={(e) => handleInputChange('middleName', e.target.value)}
						/>
						<TextField
							label="Last Name"
							value={formData.lastName}
							onChange={(e) => handleInputChange('lastName', e.target.value)}
							error={!!errors.lastName}
							helperText={errors.lastName}
							required
						/>
					</Box>

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
						<TextField
							label="Email Address"
							type="email"
							value={formData.email}
							onChange={(e) => handleInputChange('email', e.target.value)}
							error={!!errors.email}
							helperText={errors.email || 'Must be unique'}
							required
						/>
						<TextField
							label="Cell Phone"
							value={formData.cellPhone}
							onChange={(e) => handleInputChange('cellPhone', e.target.value)}
							error={!!errors.cellPhone}
							helperText={errors.cellPhone}
							required
						/>
					</Box>

					{/* Employment Details */}
					<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
						Employment Details
					</Typography>

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
						<TextField
							label="Employment Start Date"
							type="date"
							value={formData.employmentStartDate}
							onChange={(e) => handleInputChange('employmentStartDate', e.target.value)}
							error={!!errors.employmentStartDate}
							helperText={errors.employmentStartDate}
							InputLabelProps={{ shrink: true }}
							required
						/>
						<FormControl>
							<InputLabel>Employment Type</InputLabel>
							<Select
								value={formData.employmentType}
								onChange={(e) => handleInputChange('employmentType', e.target.value)}
								label="Employment Type"
							>
								<MenuItem value={EmploymentType.HOURLY}>Hourly</MenuItem>
								<MenuItem value={EmploymentType.SALARY}>Salary</MenuItem>
							</Select>
						</FormControl>
						<FormControl>
							<InputLabel>Role Type</InputLabel>
							<Select
								value={formData.roleType}
								onChange={(e) => handleInputChange('roleType', e.target.value)}
								label="Role Type"
							>
								<MenuItem value={RoleType.PART_TIME}>Part Time</MenuItem>
								<MenuItem value={RoleType.FULL_TIME}>Full Time</MenuItem>
							</Select>
						</FormControl>
					</Box>

					{/* Security & Hierarchy */}
					<Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
						Security & Hierarchy
					</Typography>

					<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
						<Autocomplete
							options={[...securityRoles].sort((a, b) => a.name.localeCompare(b.name))}
							getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
							value={securityRoles.find((role) => role['@id'] === formData.securityRole) || null}
							isOptionEqualToValue={(option, value) => option['@id'] === value?.['@id']}
							onChange={(event, value) => handleInputChange('securityRole', value?.['@id'] || '')}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Mabel Security Role"
									error={!!errors.securityRole}
									helperText={errors.securityRole}
									required
								/>
							)}
						/>
						<Autocomplete
							options={[...hierarchyNodes].sort((a, b) => {
								// First, sort by type priority: organization > group > workplace
								const getTypePriority = (type: string) => {
									if (type === 'organization') return 0;
									if (type === 'group') return 1;
									if (type === 'workplace') return 2;
									return 3;
								};

								const aPriority = getTypePriority(a.type);
								const bPriority = getTypePriority(b.type);

								if (aPriority !== bPriority) {
									return aPriority - bPriority;
								}

								// If types are the same, sort alphabetically for custom nodes
								if (a.type === 'group' && b.type === 'group') {
									return cleanHierarchyName(a.name).localeCompare(
										cleanHierarchyName(b.name),
										undefined,
										{ sensitivity: 'base' }
									);
								}

								// For workplaces, sort by ID-Name
								if (a.type === 'workplace' && b.type === 'workplace') {
									const aId = a.id || 0;
									const bId = b.id || 0;
									if (aId !== bId) {
										return aId - bId;
									}
									return cleanHierarchyName(a.name).localeCompare(
										cleanHierarchyName(b.name),
										undefined,
										{ sensitivity: 'base' }
									);
								}

								// Default alphabetical sort (cleaned)
								return cleanHierarchyName(a.name).localeCompare(
									cleanHierarchyName(b.name),
									undefined,
									{ sensitivity: 'base' }
								);
							})}
							getOptionLabel={(option) => {
								if (typeof option === 'string') return option;
								if (option.type === 'workplace') {
									const id = String(option.id).padStart(3, '0');
									return `${id} - ${cleanHierarchyName(option.name)}`;
								}
								return cleanHierarchyName(option.name);
							}}
							value={
								hierarchyNodes?.find((node) => node['@id'] === formData.hierarchyAssignment) || null
							}
							isOptionEqualToValue={(option, value) => option?.['@id'] === value?.['@id']}
							onChange={(event, value) =>
								handleInputChange('hierarchyAssignment', value?.['@id'] || '')
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Hierarchy Assignment"
									error={!!errors.hierarchyAssignment}
									helperText={errors.hierarchyAssignment}
									required
								/>
							)}
						/>
					</Box>

					{/* Action Buttons */}
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
						<Button
							type="submit"
							variant="contained"
							disabled={isSubmitting}
							sx={{
								backgroundColor: 'rgb(68, 99, 136)',
								'&:hover': { backgroundColor: 'rgb(58, 89, 126)' },
							}}
						>
							{isSubmitting ? <CircularProgress size={24} /> : 'Create'}
						</Button>
					</Box>
				</form>
			</Paper>
		</div>
	);
}
